import React, { useEffect, useMemo, useRef } from 'react';
import * as d3 from 'd3';

const NetworkGraph = ({ data, width = '100%', height = '100%', showLegend = false, showClusters = false, highlightedClusterId = null, onClusterStats, memberMap = null, showTeams = false, userGroups = null, highlightedTeamId = null, onTeamStats, onAlignmentScore }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const zoomControlsRef = useRef({ zoom: null, svg: null });
  const highlightedClusterRef = useRef(highlightedClusterId);
  const highlightedTeamRef = useRef(highlightedTeamId);
  let isDragging = false;

  const clamp01 = (n) => Math.max(0, Math.min(1, n));

  const hexToRgb = (hex) => {
    const h = String(hex || '').replace('#', '').trim();
    if (h.length !== 6) return { r: 0, g: 0, b: 0 };
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  };

  const rgbToHex = ({ r, g, b }) => {
    const to2 = (v) => String(Math.max(0, Math.min(255, Math.round(v))).toString(16)).padStart(2, '0');
    return `#${to2(r)}${to2(g)}${to2(b)}`;
  };

  const lerp = (a, b, t) => a + (b - a) * t;
  const lerpColor = (aHex, bHex, t) => {
    const a = hexToRgb(aHex);
    const b = hexToRgb(bHex);
    return rgbToHex({
      r: lerp(a.r, b.r, t),
      g: lerp(a.g, b.g, t),
      b: lerp(a.b, b.b, t),
    });
  };

  // Figma "private-balanced-public" 3-stop gradient (exact endpoints)
  const PRIVATE_FOCUSED = '#0D0D73';
  const BALANCED = '#A3297A';
  const PUBLIC_FOCUSED = '#FFCC66';

  // Map t in [0..1] where 0 = private-focused, 0.5 = balanced, 1 = public-focused
  const magma3 = (tRaw) => {
    const t = clamp01(tRaw);
    if (t <= 0.5) return lerpColor(PRIVATE_FOCUSED, BALANCED, t / 0.5);
    return lerpColor(BALANCED, PUBLIC_FOCUSED, (t - 0.5) / 0.5);
  };

  const graphSignature = useMemo(() => {
    if (!data?.nodes || !data?.links) return '';
    const nodesCount = data.nodes.length;
    const linksCount = data.links.length;
    const sampleNode = data.nodes[0]?.id ?? '';
    const sampleLink = data.links[0]
      ? `${typeof data.links[0].source === 'object' ? data.links[0].source.id : data.links[0].source}-${typeof data.links[0].target === 'object' ? data.links[0].target.id : data.links[0].target}`
      : '';
    return `${nodesCount}|${linksCount}|${sampleNode}|${sampleLink}`;
  }, [data]);
  
  // Create a simulation for the force-directed graph
  useEffect(() => {
    if (!data || !data.nodes || !data.links || data.nodes.length === 0) {
      return;
    }
    
    // Get the container dimensions if width/height are specified as percentages
    let numericWidth = width;
    let numericHeight = height;
    
    if (containerRef.current) {
      if (typeof width === 'string' && width.includes('%')) {
        numericWidth = containerRef.current.clientWidth || 800;
      }
      if (typeof height === 'string' && height.includes('%')) {
        numericHeight = containerRef.current.clientHeight || 500;
      }
    }

    // Clear existing SVG
    d3.select(svgRef.current).selectAll("*").remove();
    
    // Define color mapping using public index
    const getNodeColor = (node) => {
      // Use publicIndex (stored as private-channel ratio in our data) if available.
      if (typeof node.publicIndex === 'number') {
        const privateRatio = clamp01(node.publicIndex);
        const publicRatio = 1 - privateRatio;
        return magma3(publicRatio);
      }
      
      // Legacy fallback for old data format
      switch(node.group || node) {
        case 1: return PUBLIC_FOCUSED;
        case 2: return PRIVATE_FOCUSED;
        case 3: return BALANCED;
        default: return "#6B7280"; // Default gray
      }
    };
    
    // Create SVG element with proper numeric dimensions
    const svg = d3.select(svgRef.current)
      .attr("viewBox", `0 0 ${numericWidth} ${numericHeight}`)
      .attr("width", "100%")
      .attr("height", "100%");

    // Add an invisible background rect to capture zoom/pan gestures (must be behind graph)
    const background = svg.insert("rect", ":first-child")
      .attr("class", "zoom-background")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", numericWidth)
      .attr("height", numericHeight)
      .attr("fill", "transparent")
      .style("cursor", "grab");

    // Main zoomable layer + graph layer
    const zoomLayer = svg.append("g").attr("class", "zoom-layer");
    const graph = zoomLayer.append("g").attr("class", "graph-layer");
    
    const TOOLTIP_WIDTH_PX = 260;
    const VIEWPORT_PAD = 8;

    /** Fixed tooltip; clamps to viewport after layout so bottom rows stay visible. */
    const positionTooltip = (tooltipSel, event) => {
      const node = tooltipSel.node();
      const cx = event.clientX;
      const cy = event.clientY;
      tooltipSel
        .style("position", "fixed")
        .style("width", `${TOOLTIP_WIDTH_PX}px`)
        .style("max-width", `${TOOLTIP_WIDTH_PX}px`)
        .style("box-sizing", "border-box")
        .style("left", `${cx + 10}px`)
        .style("top", `${cy - 28}px`);

      requestAnimationFrame(() => {
        if (!node) return;
        const rect = node.getBoundingClientRect();
        let left = rect.left;
        let top = rect.top;
        if (rect.right > window.innerWidth - VIEWPORT_PAD) {
          left -= rect.right - (window.innerWidth - VIEWPORT_PAD);
        }
        if (rect.left < VIEWPORT_PAD) {
          left += VIEWPORT_PAD - rect.left;
        }
        if (rect.bottom > window.innerHeight - VIEWPORT_PAD) {
          top -= rect.bottom - (window.innerHeight - VIEWPORT_PAD);
        }
        if (rect.top < VIEWPORT_PAD) {
          top += VIEWPORT_PAD - rect.top;
        }
        tooltipSel.style("left", `${left}px`).style("top", `${top}px`);
      });
    };

    const memberFromMap = (map, id) => {
      if (!map || id == null) return undefined;
      return map.get(id) ?? map.get(String(id));
    };

    // Create a tooltip (Figma: DataPopover)
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "tooltip")
      .style("position", "fixed")
      .style("background-color", "#ffffff")
      .style("border", "1px solid #d4dbe1")
      .style("border-radius", "8px")
      .style("padding", "0px")
      .style("font-size", "12px")
      .style("font-family", "Schibsted Grotesk, system-ui, -apple-system, Segoe UI, Roboto, sans-serif")
      .style("box-shadow", "0px 1px 5px rgba(28,27,34,0.1)")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("width", `${TOOLTIP_WIDTH_PX}px`)
      .style("max-width", `${TOOLTIP_WIDTH_PX}px`)
      .style("box-sizing", "border-box")
      .style("overflow", "hidden")
      .style("z-index", "9999");
    
    // Deep clone the data to avoid modifying the original
    const nodesCopy = JSON.parse(JSON.stringify(data.nodes));
    const linksCopy = JSON.parse(JSON.stringify(data.links));
    
    // Graph bounds (leave no reserved space when legend hidden)
    const graphHeight = showLegend ? (numericHeight - 40) : numericHeight;
    const centerX = numericWidth / 2;
    const centerY = graphHeight / 2;
    
    // Scale forces with node count so large graphs don't explode to the edges
    const n = nodesCopy.length;
    const chargeStrength = -300 / Math.max(1, Math.sqrt(n) / 4);
    const nodeRadius = 4; // 8px diameter

    // Radial soft boundary to avoid "boxed" topology
    const createRadialBoundsForce = ({ centerX, centerY, radius, strength = 0.05 }) => {
      let nodes = [];
      function force() {
        for (const node of nodes) {
          if (node.x == null || node.y == null) continue;
          const dx = node.x - centerX;
          const dy = node.y - centerY;
          const dist = Math.sqrt(dx * dx + dy * dy) || 0.0001;
          const maxR = Math.max(1, radius - nodeRadius);
          if (dist > maxR) {
            const excess = dist - maxR;
            const nx = dx / dist;
            const ny = dy / dist;
            node.vx -= nx * excess * strength;
            node.vy -= ny * excess * strength;
          }
        }
      }
      force.initialize = (_nodes) => {
        nodes = _nodes || [];
      };
      return force;
    };

    // Community clustering via label propagation (fast, works well for ~150 nodes)
    const getNodeId = (n) => (typeof n === 'object' && n ? n.id : n);
    const adjacency = new Map();
    nodesCopy.forEach((n) => adjacency.set(n.id, []));
    linksCopy.forEach((l) => {
      const s = getNodeId(l.source);
      const t = getNodeId(l.target);
      if (!adjacency.has(s)) adjacency.set(s, []);
      if (!adjacency.has(t)) adjacency.set(t, []);
      adjacency.get(s).push(t);
      adjacency.get(t).push(s);
    });

    const labels = new Map(nodesCopy.map((n) => [n.id, n.id]));
    const maxIterations = 12;
    for (let iter = 0; iter < maxIterations; iter++) {
      let changed = 0;
      for (const n of nodesCopy) {
        const neighbors = adjacency.get(n.id) || [];
        if (neighbors.length === 0) continue;
        const counts = new Map();
        for (const nb of neighbors) {
          const lab = labels.get(nb);
          if (!lab) continue;
          counts.set(lab, (counts.get(lab) || 0) + 1);
        }
        let bestLabel = labels.get(n.id);
        let bestCount = -1;
        for (const [lab, cnt] of counts.entries()) {
          if (cnt > bestCount || (cnt === bestCount && String(lab) < String(bestLabel))) {
            bestLabel = lab;
            bestCount = cnt;
          }
        }
        if (bestLabel !== labels.get(n.id)) {
          labels.set(n.id, bestLabel);
          changed++;
        }
      }
      if (changed === 0) break;
    }

    // Compress labels to small community indices
    const labelToCommunity = new Map();
    let nextCommunity = 0;
    for (const n of nodesCopy) {
      const lab = labels.get(n.id) ?? n.id;
      if (!labelToCommunity.has(lab)) labelToCommunity.set(lab, nextCommunity++);
      n.community = labelToCommunity.get(lab);
    }

    // Compute degree and mark isolated nodes
    nodesCopy.forEach((n) => {
      const deg = (adjacency.get(n.id) || []).length;
      n.degree = deg;
      n.isIsolated = deg === 0;
    });

    const communitiesCount = Math.max(1, nextCommunity);
    const communityCenters = new Map();
    const ringR = Math.max(40, Math.min(numericWidth, graphHeight) * 0.18);
    for (let i = 0; i < communitiesCount; i++) {
      const a = (i / communitiesCount) * Math.PI * 2;
      communityCenters.set(i, {
        x: centerX + Math.cos(a) * ringR,
        y: centerY + Math.sin(a) * ringR,
      });
    }
    
    const deriveOverlap = (leftIds, rightIds) => {
      const leftSet = new Set(leftIds);
      const rightSet = new Set(rightIds);
      let sharedCount = 0;
      leftSet.forEach((id) => {
        if (rightSet.has(id)) sharedCount += 1;
      });
      const leftCount = leftSet.size;
      const rightCount = rightSet.size;
      const unionCount = leftCount + rightCount - sharedCount;
      const pctOfLeft = leftCount > 0 ? Math.round((sharedCount / leftCount) * 100) : 0;
      const pctOfRight = rightCount > 0 ? Math.round((sharedCount / rightCount) * 100) : 0;
      const jaccardPct = unionCount > 0 ? Math.round((sharedCount / unionCount) * 100) : 0;
      return { sharedCount, pctOfLeft, pctOfRight, jaccardPct };
    };

    const nodeIdSet = new Set(nodesCopy.map(n => n.id));
    const nodeById = new Map(nodesCopy.map(n => [n.id, n]));

    // Compute per-cluster metrics and expose via callback
    const computeClusterStats = () => {
      const nodeByCommunity = new Map();
      nodesCopy.forEach(n => {
        if (n.isIsolated) return;
        const c = n.community ?? 0;
        if (!nodeByCommunity.has(c)) nodeByCommunity.set(c, []);
        nodeByCommunity.get(c).push(n);
      });

      const nodeComm = new Map();
      nodesCopy.forEach(n => { if (!n.isIsolated) nodeComm.set(n.id, n.community ?? 0); });

      const stats = new Map();
      nodeByCommunity.forEach((members, communityId) => {
        let internalLinks = 0;
        let externalLinks = 0;
        linksCopy.forEach(l => {
          const s = getNodeId(l.source);
          const t = getNodeId(l.target);
          const sComm = nodeComm.get(s);
          const tComm = nodeComm.get(t);
          if (sComm === communityId && tComm === communityId) {
            internalLinks++;
          } else if (sComm === communityId || tComm === communityId) {
            externalLinks++;
          }
        });

        const memberCount = members.length;
        const memberIds = members.map(m => m.id);
        const possibleLinks = memberCount * (memberCount - 1) / 2;
        const internalDensity = possibleLinks > 0 ? Math.round((internalLinks / possibleLinks) * 100) : 0;
        const totalLinks = internalLinks + externalLinks;
        const outboundRate = totalLinks > 0 ? Math.round((externalLinks / totalLinks) * 100) : 0;

        const avgPrivacy = memberCount > 0
          ? Math.round(members.reduce((s, m) => s + (typeof m.publicIndex === 'number' ? m.publicIndex : 0), 0) / memberCount * 100)
          : 0;

        const bridgeThreshold = 0.15;
        const bridgeNodes = members.filter(m => {
          if (typeof m.betweennessCentrality !== 'number' || m.betweennessCentrality < bridgeThreshold) return false;
          const neighbors = adjacency.get(m.id) || [];
          return neighbors.some(nbId => {
            const nbComm = nodeComm.get(nbId);
            return nbComm !== undefined && nbComm !== communityId;
          });
        }).length;

        const roleCounts = {};
        members.forEach(m => {
          const t = memberFromMap(memberMap, m.id)?.title;
          if (t) roleCounts[t] = (roleCounts[t] || 0) + 1;
        });
        const roles = Object.entries(roleCounts)
          .map(([title, count]) => ({ title, count }))
          .sort((a, b) => b.count - a.count);

        const teamOverlaps = [];
        if (userGroups) {
          userGroups.forEach(group => {
            const groupIds = (group.userIds || []).filter(id => nodeIdSet.has(id));
            const overlap = deriveOverlap(memberIds, groupIds);
            if (overlap.sharedCount > 0) {
              teamOverlaps.push({
                usergroupId: group.usergroupId || group._id,
                name: group.name,
                handle: group.handle,
                ...overlap,
              });
            }
          });
        }
        teamOverlaps.sort((a, b) => (
          (b.jaccardPct - a.jaccardPct) ||
          (b.sharedCount - a.sharedCount) ||
          (b.pctOfLeft - a.pctOfLeft) ||
          (a.name || "").localeCompare(b.name || "")
        ));
        const bestTeam = teamOverlaps.length > 0
          ? {
              usergroupId: teamOverlaps[0].usergroupId,
              name: teamOverlaps[0].name,
              handle: teamOverlaps[0].handle,
              sharedCount: teamOverlaps[0].sharedCount,
              pct: teamOverlaps[0].pctOfLeft,
              pctOfTeam: teamOverlaps[0].pctOfRight,
              jaccardPct: teamOverlaps[0].jaccardPct,
            }
          : null;

        stats.set(communityId, {
          communityId,
          members: memberCount,
          internalLinks,
          externalLinks,
          internalDensity,
          outboundRate,
          avgPrivacy,
          bridgeNodes,
          roles,
          bestTeam,
          _nodeIds: memberIds,
        });
      });
      return stats;
    };

    const clusterStatsMap = computeClusterStats();
    if (onClusterStats) {
      const statsArray = Array.from(clusterStatsMap.values())
        .sort((a, b) => b.members - a.members);
      onClusterStats(statsArray);
    }

    // Compute per-team (user group) stats and expose via callback
    const computeTeamStats = () => {
      if (!userGroups || userGroups.length === 0) return [];
      return userGroups.map(group => {
        // Prefer explicit userIds from directory payload to avoid stale/approximate count fields.
        const totalMembers = Array.isArray(group.userIds)
          ? group.userIds.length
          : (typeof group.userCount === 'number' ? group.userCount : 0);
        const groupNodeIds = (group.userIds || []).filter(id => nodeIdSet.has(id));
        const groupNodeIdSet = new Set(groupNodeIds);
        const groupMembers = groupNodeIds.map(id => nodeById.get(id)).filter(Boolean);
        const memberCount = groupMembers.length;

        let internalLinks = 0;
        let externalLinks = 0;
        linksCopy.forEach(l => {
          const s = getNodeId(l.source);
          const t = getNodeId(l.target);
          const sIn = groupNodeIdSet.has(s);
          const tIn = groupNodeIdSet.has(t);
          if (sIn && tIn) internalLinks++;
          else if (sIn || tIn) externalLinks++;
        });

        const possibleLinks = memberCount * (memberCount - 1) / 2;
        const internalDensity = possibleLinks > 0 ? Math.round((internalLinks / possibleLinks) * 100) : 0;
        const totalLinks = internalLinks + externalLinks;
        const outboundRate = totalLinks > 0 ? Math.round((externalLinks / totalLinks) * 100) : 0;
        const avgPrivacy = memberCount > 0
          ? Math.round(groupMembers.reduce((s, m) => s + (typeof m.publicIndex === 'number' ? m.publicIndex : 0), 0) / memberCount * 100)
          : 0;

        const roleCounts = {};
        groupMembers.forEach(m => {
          const t = memberFromMap(memberMap, m.id)?.title;
          if (t) roleCounts[t] = (roleCounts[t] || 0) + 1;
        });
        const roles = Object.entries(roleCounts)
          .map(([title, count]) => ({ title, count }))
          .sort((a, b) => b.count - a.count);

        const clusterOverlaps = Array.from(clusterStatsMap.values()).map(cluster => {
          const clusterNodeIds = nodesCopy
            .filter(n => !n.isIsolated && (n.community ?? 0) === cluster.communityId)
            .map(n => n.id);
          return {
            communityId: cluster.communityId,
            ...deriveOverlap(groupNodeIds, clusterNodeIds),
          };
        }).filter(o => o.sharedCount > 0);

        clusterOverlaps.sort((a, b) => (
          (b.jaccardPct - a.jaccardPct) ||
          (b.sharedCount - a.sharedCount) ||
          (b.pctOfLeft - a.pctOfLeft) ||
          (a.communityId - b.communityId)
        ));
        const bestCluster = clusterOverlaps.length > 0
          ? {
              communityId: Number(clusterOverlaps[0].communityId),
              sharedCount: clusterOverlaps[0].sharedCount,
              pct: clusterOverlaps[0].pctOfLeft,
              pctOfCluster: clusterOverlaps[0].pctOfRight,
              jaccardPct: clusterOverlaps[0].jaccardPct,
            }
          : null;

        return {
          usergroupId: group.usergroupId || group._id,
          name: group.name,
          handle: group.handle,
          members: memberCount,
          totalMembers,
          internalDensity,
          outboundRate,
          avgPrivacy,
          roles,
          bestCluster,
          _nodeIds: groupNodeIds,
        };
      }).filter(t => t.members > 0).sort((a, b) => b.members - a.members);
    };

    const teamStatsArray = computeTeamStats();
    const teamStatsMap = new Map(teamStatsArray.map(t => [t.usergroupId, t]));
    if (onTeamStats) {
      onTeamStats(teamStatsArray);
    }

    if (onAlignmentScore) {
      let weightedSum = 0;
      let totalMembers = 0;
      teamStatsArray.forEach(t => {
        if (t.bestCluster) {
          weightedSum += t.members * t.bestCluster.jaccardPct;
          totalMembers += t.members;
        }
      });
      const score = totalMembers > 0 ? Math.round(weightedSum / totalMembers) : 0;
      onAlignmentScore(score);
    }

    // Create a force simulation for a natural, centered network layout
    // Avoid "globular/ring" layouts: do NOT force nodes onto a radius.
    // Instead, keep things centered and pull higher-degree nodes inward.
    const radialRadius = Math.max(120, Math.min(numericWidth, graphHeight) / 2 - 40);
    const isolatesCenterX = centerX + radialRadius * 0.85;
    const isolatesCenterY = centerY + radialRadius * 0.25;

    const maxDegree = Math.max(1, ...nodesCopy.map(n => (typeof n.degree === 'number' ? n.degree : 0)));
    const degreeNorm = (d) => {
      const deg = typeof d.degree === 'number' ? d.degree : 0;
      return Math.min(1, Math.max(0, deg / maxDegree));
    };
    const simulation = d3.forceSimulation(nodesCopy)
      .force("link", d3.forceLink(linksCopy).id(d => d.id).distance((l) => {
        const v = typeof l.value === 'number' ? l.value : 1;
        // Stronger ties pull closer, but keep a reasonable floor to avoid hairballs
        return Math.max(35, 90 - Math.log1p(Math.max(0, v)) * 18);
      }).iterations(1))
      .force("charge", d3.forceManyBody().strength(chargeStrength))
      .force("center", d3.forceCenter(centerX, centerY))
      .force("x", d3.forceX(d => (d.isIsolated ? isolatesCenterX : centerX)).strength(d => {
        if (d.isIsolated) return 0.12;
        // Pull connected / high-degree nodes inward slightly more
        return 0.03 + degreeNorm(d) * 0.08;
      }))
      .force("y", d3.forceY(d => (d.isIsolated ? isolatesCenterY : centerY)).strength(d => {
        if (d.isIsolated) return 0.12;
        return 0.03 + degreeNorm(d) * 0.08;
      }))
      .force("collide", d3.forceCollide().radius(d => (typeof d.value === 'number' ? Math.sqrt(d.value) + nodeRadius : nodeRadius) + 4))
      // Keep a very soft bounds force to prevent runaway, but avoid visible rims
      .force("bounds", createRadialBoundsForce({ centerX, centerY, radius: radialRadius * 1.35, strength: 0.015 }))
      .force("clusterX", d3.forceX((d) => (communityCenters.get(d.community)?.x ?? centerX)).strength(d => (d.isIsolated ? 0 : 0.06)))
      .force("clusterY", d3.forceY((d) => (communityCenters.get(d.community)?.y ?? centerY)).strength(d => (d.isIsolated ? 0 : 0.06)));
    
    // Create hull layers
    const clustersLayer = zoomLayer.append("g").attr("class", "clusters-layer");
    const teamsLayer = zoomLayer.append("g").attr("class", "teams-layer");

    const link = graph.append("g")
      .attr("stroke", "#999")
      .attr("stroke-opacity", 0.6)
      .selectAll("line")
      .data(linksCopy)
      .join("line")
      .attr("stroke-width", 1); // Fixed 1px width
    
    // Create the nodes (made larger)
    const node = graph.append("g")
      .selectAll("circle")
      .data(nodesCopy)
      .join("circle")
      .attr("r", nodeRadius)
      .attr("fill", d => getNodeColor(d))
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5)
      .call(drag(simulation, () => { isDragging = true; }, () => { isDragging = false; }))
      .on("mouseover", function(event, d) {
        d3.select(this).attr("stroke", "#000").attr("stroke-width", 3);
        tooltip.transition()
          .duration(200)
          .style("opacity", 1);
        
        const escapeHtml = (str) => (
          String(str ?? '')
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#039;')
        );

        const INDICATOR_URL = "https://www.figma.com/api/mcp/asset/517eaf58-fb73-4f16-bc5c-ceb57d5f1f2d";

        const metricBadge = (pct) => {
          const p = Math.max(0, Math.min(100, Math.round(pct)));
          if (p === 0) return { label: 'None', bg: '#f0f2f4' };
          if (p < 34) return { label: 'Low', bg: '#fbdada' };
          if (p < 67) return { label: 'Medium', bg: '#fbf0da' };
          return { label: 'High', bg: '#dafbeb' };
        };

        // Silo index = private-channel share (same scale as org silo index); lower is less siloed.
        const siloBadge = (siloPct) => {
          const p = Math.max(0, Math.min(100, Math.round(siloPct)));
          if (p <= 33) return { label: 'Low', bg: '#dafbeb' };
          if (p <= 66) return { label: 'Medium', bg: '#fbf0da' };
          return { label: 'High', bg: '#fbdada' };
        };

        // Calculate interaction details (anonymized - no user names)
        const userLinks = linksCopy.filter(l => 
          (typeof l.source === 'object' ? l.source.id : l.source) === d.id || 
          (typeof l.target === 'object' ? l.target.id : l.target) === d.id
        );
        
        const memberInfo = memberFromMap(memberMap, d.id);

        // Ratios
        const privateRatio = typeof d.publicIndex === 'number' ? clamp01(d.publicIndex) : 0;
        const publicRatio = 1 - privateRatio;

        // Message counts (actual posts). publicInteractions/privateInteractions are weighted graph edges, not messages.
        const privMsgs = d.channelActivity?.privateMessages ?? d.channelActivity?.privateInteractions ?? 0;
        const pubMsgs = d.channelActivity?.publicMessages ?? d.channelActivity?.publicInteractions ?? 0;

        // Node silo index = private-channel share from publicIndex (0–1 → %)
        const siloIndexPct = typeof d.publicIndex === 'number' ? Math.round(clamp01(d.publicIndex) * 100) : 0;
        const siloIndexBadge = siloBadge(siloIndexPct);

        // Influence & reach (metrics)
        const connectivityPct = typeof d.degreeCentrality === 'number' ? Math.round(d.degreeCentrality * 100) : 0;
        const bridgePct = typeof d.betweennessCentrality === 'number' ? Math.round(d.betweennessCentrality * 100) : 0;
        const reachabilityPct = typeof d.closenessCentrality === 'number' ? Math.round(d.closenessCentrality * 100) : 0;
        const influencePct = typeof d.eigenvectorCentrality === 'number' ? Math.round(d.eigenvectorCentrality * 100) : 0;

        const connectivityBadge = metricBadge(connectivityPct);
        const bridgeBadge = metricBadge(bridgePct);
        const reachabilityBadge = metricBadge(reachabilityPct);
        const influenceBadge = metricBadge(influencePct);

        const roleTitle = escapeHtml(d.title || memberInfo?.title || d.name || memberInfo?.name || 'Member');

        // Indicator placement: left = private-focused, right = public-focused
        const indicatorLeftPct = Math.round(publicRatio * 1000) / 10; // 1 decimal for smoothness

        const row = (label, valueHtml) => `
          <div style="display:flex;align-items:center;justify-content:space-between;height:24px;font-size:14px;line-height:22px;letter-spacing:-0.5px;color:#04101a;white-space:nowrap;">
            <div style="font-weight:400;">${label}</div>
            <div style="font-weight:500;">${valueHtml}</div>
          </div>
        `;

        const metricRow = (label, pctValue, badge) => `
          <div style="display:flex;gap:4px;align-items:baseline;height:24px;width:100%;">
            <div style="flex:1 0 0;font-weight:400;font-size:14px;line-height:22px;letter-spacing:-0.5px;color:#04101a;">${label}</div>
            <div style="font-weight:500;font-size:14px;line-height:22px;letter-spacing:-0.5px;color:#04101a;white-space:nowrap;">${Math.max(0, Math.min(100, Math.round(pctValue)))}%</div>
            <div style="background:${badge.bg};display:flex;align-items:center;justify-content:center;height:24px;padding:0 8px;border-radius:6px;">
              <div style="font-weight:500;font-size:14px;line-height:22px;letter-spacing:0;color:#04101a;white-space:nowrap;">${badge.label}</div>
            </div>
          </div>
        `;

        const tooltipContent = `
          <div style="background:#ffffff;border:1px solid #d4dbe1;border-radius:8px;box-shadow:0px 1px 5px rgba(28,27,34,0.1);overflow:hidden;">
            <div style="border-bottom:1px solid #f0f2f4;padding:12px 16px;">
              <div style="font-weight:600;font-size:16px;line-height:22px;letter-spacing:-0.5px;color:#04101a;white-space:nowrap;">${roleTitle}</div>
            </div>

            <div style="border-bottom:1px solid #f0f2f4;padding:12px 16px;display:flex;flex-direction:column;gap:4px;">
              <div style="font-weight:500;font-size:12px;line-height:20px;letter-spacing:0.5px;text-transform:uppercase;color:#575c61;white-space:nowrap;">Influence &amp; reach</div>
              ${row('Total connections', escapeHtml(userLinks.length))}
              ${metricRow('Connectivity', connectivityPct, connectivityBadge)}
              ${metricRow('Bridge role', bridgePct, bridgeBadge)}
              ${metricRow('Reachability', reachabilityPct, reachabilityBadge)}
              ${metricRow('Influence', influencePct, influenceBadge)}
            </div>

            <div style="padding:12px 16px;display:flex;flex-direction:column;gap:4px;">
              <div style="font-weight:500;font-size:12px;line-height:20px;letter-spacing:0.5px;text-transform:uppercase;color:#575c61;white-space:nowrap;">Siloing</div>
              ${metricRow('Silo index', siloIndexPct, siloIndexBadge)}

              <div style="padding-top:8px;display:flex;flex-direction:column;gap:2px;width:100%;">
                <div style="position:relative;height:8px;width:100%;background:linear-gradient(90deg, ${PRIVATE_FOCUSED} 0%, ${BALANCED} 49.519%, ${PUBLIC_FOCUSED} 100%);">
                  <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:1px;height:12px;background:#ffffff;"></div>
                </div>
                <div style="position:relative;display:flex;align-items:center;justify-content:space-between;height:4px;width:100%;">
                  ${Array.from({ length: 11 }).map(() => `<div style="width:1px;height:4px;background:#c0cbd4;"></div>`).join('')}
                  <div style="position:absolute;left:${indicatorLeftPct}%;top:-14px;transform:translateX(-50%);width:9px;height:18px;">
                    <img alt="" src="${INDICATOR_URL}" style="display:block;width:100%;height:100%;" />
                  </div>
                </div>
              </div>

              ${row('Public channels', `${escapeHtml(pubMsgs)} messages`)}
              ${row('Private channels', `${escapeHtml(privMsgs)} messages`)}
            </div>
          </div>
        `;
        
        tooltip.html(tooltipContent);
        positionTooltip(tooltip, event);
      })
      .on("mouseout", function() {
        d3.select(this).attr("stroke", "#fff").attr("stroke-width", 2);
        tooltip.transition()
          .duration(500)
          .style("opacity", 0);
      });

    // Selection + highlight state (in closure)
    let selectedNodeId = null;
    const selectedNeighborIds = new Set();

    const recomputeHighlight = () => {
      node
        .attr("fill", d => {
          if (!selectedNodeId) return getNodeColor(d);
          if (d.id === selectedNodeId) return "#00AAFF";
          return getNodeColor(d);
        })
        .attr("opacity", d => {
          if (!selectedNodeId) return 1;
          if (d.id === selectedNodeId) return 1;
          if (selectedNeighborIds.has(d.id)) return 1;
          return 0.2;
        });

      link
        .attr("stroke", l => {
          if (!selectedNodeId) return "#999";
          const s = getNodeId(l.source);
          const t = getNodeId(l.target);
          const incident = s === selectedNodeId || t === selectedNodeId;
          return incident ? "#00AAFF" : "#CACFD8";
        })
        .attr("stroke-opacity", l => {
          if (!selectedNodeId) return 0.6;
          const s = getNodeId(l.source);
          const t = getNodeId(l.target);
          const incident = s === selectedNodeId || t === selectedNodeId;
          return incident ? 0.9 : 0.15;
        })
        .attr("stroke-width", l => {
          if (!selectedNodeId) return 1;
          const s = getNodeId(l.source);
          const t = getNodeId(l.target);
          const incident = s === selectedNodeId || t === selectedNodeId;
          return incident ? 1.8 : 1;
        });
    };

    node.on("click", function(event, d) {
      if (isDragging) return;
      event.stopPropagation();
      if (selectedNodeId === d.id) {
        selectedNodeId = null;
        selectedNeighborIds.clear();
      } else {
        selectedNodeId = d.id;
        selectedNeighborIds.clear();
        (adjacency.get(d.id) || []).forEach(id => selectedNeighborIds.add(id));
      }
      recomputeHighlight();
    });
    
    // REMOVED: Node labels to maintain anonymity
    // No text labels or titles displayed to prevent user identification
    
    // Initialize node positions in a tight cluster so forces pull them to a natural layout
    nodesCopy.forEach(d => {
      d.x = centerX + (Math.random() - 0.5) * Math.min(numericWidth, graphHeight) * 0.3;
      d.y = centerY + (Math.random() - 0.5) * Math.min(numericWidth, graphHeight) * 0.3;
    });
    
    let tickCount = 0;
    // Update positions on each tick (no hard clamping; bounds force keeps things reasonable)
    simulation.on("tick", () => {
      link
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y);
      
      node
        .attr("cx", d => d.x)
        .attr("cy", d => d.y);

      tickCount += 1;

      // Update cluster hulls occasionally once layout forms (only when Clusters tab is active)
      if (showClusters && (tickCount % 4 === 0 || simulation.alpha() > 0.2)) {
        const communitiesPoints = new Map();
        nodesCopy.forEach(n => {
          if (n.isIsolated) return;
          const key = n.community ?? 0;
          if (!communitiesPoints.has(key)) communitiesPoints.set(key, []);
          communitiesPoints.get(key).push([n.x, n.y]);
        });

        const hulls = [];
        communitiesPoints.forEach((pts, key) => {
          if (pts.length < 3) return;
          const hull = d3.polygonHull(pts);
          if (!hull) return;
          let d = `M${hull[0][0]},${hull[0][1]}`;
          for (let i = 1; i < hull.length; i++) {
            d += `L${hull[i][0]},${hull[i][1]}`;
          }
          d += "Z";
          hulls.push({ community: key, d });
        });

        const clusterPaths = clustersLayer
          .selectAll("path")
          .data(hulls, h => h.community);

        clusterPaths
          .join(
            enter => enter.append("path")
              .attr("fill", "#31048B")
              .attr("fill-opacity", h => {
                const hl = highlightedClusterRef.current;
                if (hl == null) return 0.03;
                return h.community === hl ? 0.12 : 0.01;
              })
              .attr("stroke", "#31048B")
              .attr("stroke-opacity", h => {
                const hl = highlightedClusterRef.current;
                if (hl == null) return 0.06;
                return h.community === hl ? 0.25 : 0.03;
              })
              .attr("stroke-width", h => {
                const hl = highlightedClusterRef.current;
                return (hl != null && h.community === hl) ? 2 : 1;
              })
              .attr("pointer-events", "visiblePainted")
              .style("cursor", "pointer")
              .attr("d", h => h.d)
              .on("mouseenter", function(event, h) {
                if (highlightedClusterRef.current == null) {
                  d3.select(this).attr("fill-opacity", 0.07).attr("stroke-opacity", 0.15);
                }
                const cs = clusterStatsMap.get(h.community);
                if (!cs) return;
                const rankIndex = Array.from(clusterStatsMap.values())
                  .sort((a, b) => b.members - a.members)
                  .findIndex(s => s.communityId === h.community);
                const label = `Cluster ${rankIndex >= 0 ? rankIndex + 1 : h.community + 1}`;
                let content = `<strong>${label}</strong>`;
                content += `<br/>Members: ${cs.members}`;
                content += `<br/>Internal density: ${cs.internalDensity}%`;
                content += `<br/>Outbound collaboration: ${cs.outboundRate}%`;
                content += `<br/>Internal silo index: ${cs.avgPrivacy}%`;
                if (cs.bridgeNodes > 0) content += `<br/>Bridge nodes: ${cs.bridgeNodes}`;
                tooltip.transition().duration(200).style("opacity", 1);
                tooltip.html(content);
                positionTooltip(tooltip, event);
              })
              .on("mouseleave", function() {
                if (highlightedClusterRef.current == null) {
                  d3.select(this).attr("fill-opacity", 0.03).attr("stroke-opacity", 0.06);
                }
                tooltip.transition().duration(300).style("opacity", 0);
              }),
            update => update.attr("d", h => h.d)
              .attr("fill-opacity", h => {
                const hl = highlightedClusterRef.current;
                if (hl == null) return 0.03;
                return h.community === hl ? 0.12 : 0.01;
              })
              .attr("stroke-opacity", h => {
                const hl = highlightedClusterRef.current;
                if (hl == null) return 0.06;
                return h.community === hl ? 0.25 : 0.03;
              })
              .attr("stroke-width", h => {
                const hl = highlightedClusterRef.current;
                return (hl != null && h.community === hl) ? 2 : 1;
              }),
            exit => exit.remove()
          );
      } else if (!showClusters) {
        clustersLayer.selectAll("path").remove();
      }

      // Update team hulls (only when Teams tab is active)
      if (showTeams && teamStatsArray.length > 0 && (tickCount % 4 === 0 || simulation.alpha() > 0.2)) {
        const teamHulls = [];
        teamStatsArray.forEach(team => {
          const pts = team._nodeIds
            .map(id => nodeById.get(id))
            .filter(n => n && typeof n.x === 'number' && typeof n.y === 'number')
            .map(n => [n.x, n.y]);
          if (pts.length < 3) return;
          const hull = d3.polygonHull(pts);
          if (!hull) return;
          let pathD = `M${hull[0][0]},${hull[0][1]}`;
          for (let i = 1; i < hull.length; i++) pathD += `L${hull[i][0]},${hull[i][1]}`;
          pathD += "Z";
          teamHulls.push({ usergroupId: team.usergroupId, d: pathD, name: team.name });
        });

        const teamPaths = teamsLayer
          .selectAll("path")
          .data(teamHulls, h => h.usergroupId);

        teamPaths
          .join(
            enter => enter.append("path")
              .attr("fill", "#0066CC")
              .attr("fill-opacity", h => {
                const hl = highlightedTeamRef.current;
                if (hl == null) return 0.04;
                return h.usergroupId === hl ? 0.14 : 0.01;
              })
              .attr("stroke", "#0066CC")
              .attr("stroke-opacity", h => {
                const hl = highlightedTeamRef.current;
                if (hl == null) return 0.08;
                return h.usergroupId === hl ? 0.3 : 0.03;
              })
              .attr("stroke-width", h => {
                const hl = highlightedTeamRef.current;
                return (hl != null && h.usergroupId === hl) ? 2 : 1;
              })
              .attr("pointer-events", "visiblePainted")
              .style("cursor", "pointer")
              .attr("d", h => h.d)
              .on("mouseenter", function(event, h) {
                if (highlightedTeamRef.current == null) {
                  d3.select(this).attr("fill-opacity", 0.09).attr("stroke-opacity", 0.18);
                }
                const ts = teamStatsMap.get(h.usergroupId);
                if (!ts) return;
                let content = `<strong>${ts.name}</strong>`;
                if (ts.handle) content += ` <span style="color:#666">@${ts.handle}</span>`;
                content += `<br/>Members: ${ts.members}`;
                content += `<br/>Internal density: ${ts.internalDensity}%`;
                content += `<br/>Outbound collaboration: ${ts.outboundRate}%`;
                content += `<br/>Internal silo index: ${ts.avgPrivacy}%`;
                tooltip.transition().duration(200).style("opacity", 1);
                tooltip.html(content);
                positionTooltip(tooltip, event);
              })
              .on("mouseleave", function() {
                if (highlightedTeamRef.current == null) {
                  d3.select(this).attr("fill-opacity", 0.04).attr("stroke-opacity", 0.08);
                }
                tooltip.transition().duration(300).style("opacity", 0);
              }),
            update => update.attr("d", h => h.d)
              .attr("fill-opacity", h => {
                const hl = highlightedTeamRef.current;
                if (hl == null) return 0.04;
                return h.usergroupId === hl ? 0.14 : 0.01;
              })
              .attr("stroke-opacity", h => {
                const hl = highlightedTeamRef.current;
                if (hl == null) return 0.08;
                return h.usergroupId === hl ? 0.3 : 0.03;
              })
              .attr("stroke-width", h => {
                const hl = highlightedTeamRef.current;
                return (hl != null && h.usergroupId === hl) ? 2 : 1;
              }),
            exit => exit.remove()
          );
      } else if (!showTeams) {
        teamsLayer.selectAll("path").remove();
      }

      // Ensure highlight styling persists as nodes move
      if (selectedNodeId) {
        recomputeHighlight();
      }
    });
    
    // Let the simulation run longer for a more stable, natural layout
    simulation.alphaDecay(0.02);
    
    // Zoom + pan behavior
    const zoom = d3.zoom()
      .scaleExtent([0.25, 4])
      .filter((event) => {
        // Allow wheel/pinch; allow drag-pan on background, but don't start zoom on node drag
        const t = event?.target;
        const tag = t?.tagName?.toLowerCase();
        if (tag === 'circle') return false;
        return true;
      })
      .on("start", () => background.style("cursor", "grabbing"))
      .on("end", () => background.style("cursor", "grab"))
      .on("zoom", (event) => {
        zoomLayer.attr("transform", event.transform);
      });

    svg.call(zoom);
    svg.on("dblclick.zoom", null);
    zoomControlsRef.current = { zoom, svg };

    background.on("click", () => {
      if (!selectedNodeId) return;
      selectedNodeId = null;
      selectedNeighborIds.clear();
      recomputeHighlight();
    });

    // Fit-to-view once the simulation has had a moment to settle
    const fitPadding = 24;
    const fitTimeout = window.setTimeout(() => {
      const xs = nodesCopy.map(d => d.x).filter(v => typeof v === 'number');
      const ys = nodesCopy.map(d => d.y).filter(v => typeof v === 'number');
      if (xs.length === 0 || ys.length === 0) return;

      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const boundsW = Math.max(1, maxX - minX);
      const boundsH = Math.max(1, maxY - minY);

      const scale = Math.min(
        4,
        Math.max(
          0.25,
          0.95 * Math.min(
            numericWidth / (boundsW + fitPadding * 2),
            graphHeight / (boundsH + fitPadding * 2)
          )
        )
      );

      const midX = (minX + maxX) / 2;
      const midY = (minY + maxY) / 2;
      const translate = [
        (numericWidth / 2) - scale * midX,
        (graphHeight / 2) - scale * midY
      ];

      const transform = d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale);
      svg.transition().duration(450).call(zoom.transform, transform);
    }, 450);
    
    // Run the simulation for a few iterations to get an initial layout
    simulation.alpha(1).restart();
    
    // Clean up tooltip when component unmounts
    return () => {
      d3.select(".tooltip").remove();
      window.clearTimeout(fitTimeout);
      simulation.stop();
    };
  }, [data, width, height, showLegend, showClusters, showTeams, graphSignature, memberMap, userGroups]);

  // Reactively highlight a cluster hull when highlightedClusterId changes
  useEffect(() => {
    highlightedClusterRef.current = highlightedClusterId;
    const svg = d3.select(svgRef.current);
    const paths = svg.selectAll(".zoom-layer .clusters-layer path");
    if (paths.empty()) return;

    paths.each(function () {
      const el = d3.select(this);
      const datum = el.datum();
      if (!datum) return;
      if (highlightedClusterId == null) {
        el.attr("fill-opacity", 0.03).attr("stroke-opacity", 0.06).attr("stroke-width", 1);
      } else if (datum.community === highlightedClusterId) {
        el.attr("fill-opacity", 0.12).attr("stroke-opacity", 0.25).attr("stroke-width", 2);
      } else {
        el.attr("fill-opacity", 0.01).attr("stroke-opacity", 0.03).attr("stroke-width", 1);
      }
    });
  }, [highlightedClusterId]);

  // Reactively highlight a team hull when highlightedTeamId changes
  useEffect(() => {
    highlightedTeamRef.current = highlightedTeamId;
    const svg = d3.select(svgRef.current);
    const paths = svg.selectAll(".zoom-layer .teams-layer path");
    if (paths.empty()) return;

    paths.each(function () {
      const el = d3.select(this);
      const datum = el.datum();
      if (!datum) return;
      if (highlightedTeamId == null) {
        el.attr("fill-opacity", 0.04).attr("stroke-opacity", 0.08).attr("stroke-width", 1);
      } else if (datum.usergroupId === highlightedTeamId) {
        el.attr("fill-opacity", 0.14).attr("stroke-opacity", 0.3).attr("stroke-width", 2);
      } else {
        el.attr("fill-opacity", 0.01).attr("stroke-opacity", 0.03).attr("stroke-width", 1);
      }
    });
  }, [highlightedTeamId]);
  
  // Implement drag functionality
  function drag(simulation) {
    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }
    
    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }
    
    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }
    
    return d3.drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  }
  
  return (
    <div ref={containerRef} className="relative" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <svg ref={svgRef} width="100%" height="100%" />

      {/* Zoom controls (Figma: bottom-right, 32x64, white, grey border, r=8) */}
      <div className="absolute bottom-[15px] right-[15px] bg-white-f border border-grey-30 rounded-[8px] shadow-[0px_1px_1px_0px_rgba(28,27,34,0.1)] overflow-hidden">
        <button
          type="button"
          className="w-[32px] h-[32px] flex items-center justify-center border-b border-grey-30 hover:bg-grey-10 active:bg-grey-20"
          aria-label="Zoom in"
          onClick={() => {
            const { zoom, svg } = zoomControlsRef.current || {};
            if (!zoom || !svg) return;
            svg.transition().duration(180).call(zoom.scaleBy, 1.2);
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M12 5l0 14" />
            <path d="M5 12l14 0" />
          </svg>
        </button>
        <button
          type="button"
          className="w-[32px] h-[32px] flex items-center justify-center hover:bg-grey-10 active:bg-grey-20"
          aria-label="Zoom out"
          onClick={() => {
            const { zoom, svg } = zoomControlsRef.current || {};
            if (!zoom || !svg) return;
            svg.transition().duration(180).call(zoom.scaleBy, 1 / 1.2);
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M5 12l14 0" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NetworkGraph; 